// src/pages/Accounting/Reconcile/Session/index.tsx

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PALETTE } from "../constants";
import { mockAccounts } from "../mockData";
import { clearDraft } from "../storage";
import {
  countByTabFilter,
  filterSessionByType,
  formatDateLong,
} from "../utils";
import { FilterPopover } from "./FilterPopover";
import { HowItWorksTour } from "./HowItWorksTour";
import { MarkReconciledModal } from "./MarkReconciledModal";
import { MathEquation } from "./MathEquation";
import { PrintReport } from "./PrintReport";
import { SessionFilterBar } from "./SessionFilterBar";
import { SessionHeader } from "./SessionHeader";
import { SessionTable } from "./SessionTable";
import { TableSettings } from "./TableSettings";
import { mockSessionTransactionsRich } from "./sessionMockData";
import { EMPTY_FILTER_STATE } from "./sessionTypes";
import type {
  ColumnKey,
  Density,
  SessionFilterState,
  SessionTransactionRich,
  TabFilter,
} from "./sessionTypes";
import {
  isTourDismissed,
  loadDensity,
  loadVisibleColumns,
  saveDensity,
  saveVisibleColumns,
  setTourDismissed,
} from "./sessionStorage";

const COMPANY_NAME = "BrightCare Home Healthcare Services Inc.";

// This page is registered in AppLayout's FULL_BLEED_MAIN_PATHS set, so <main>
// renders with padding: 0 on this route. That lets position: sticky inside
// pageStyle pin flush with main's top edge (Chrome otherwise pins at the
// content box top, which left scrolled rows leaking into main's padding).
// Sub-components (SessionHeader, MathEquation, SessionFilterBar, SessionTable)
// each carry their own horizontal padding, so content stays indented correctly.

function applyFilterState(
  txs: SessionTransactionRich[],
  state: SessionFilterState,
  clearedIds: Set<string>,
): SessionTransactionRich[] {
  let result = txs;
  if (state.find.trim()) {
    const needle = state.find.trim().toLowerCase();
    result = result.filter(
      (t) =>
        (t.payee ?? "").toLowerCase().includes(needle) ||
        (t.memo ?? "").toLowerCase().includes(needle) ||
        (t.refNo ?? "").toLowerCase().includes(needle),
    );
  }
  if (state.transactionType !== "all") {
    result = result.filter((t) => t.kind === state.transactionType);
  }
  if (state.payee !== "all") {
    result = result.filter((t) => t.payee === state.payee);
  }
  if (state.clearedStatus === "cleared") {
    result = result.filter((t) => clearedIds.has(t.id));
  } else if (state.clearedStatus === "uncleared") {
    result = result.filter((t) => !clearedIds.has(t.id));
  } else if (state.clearedStatus === "needs-review") {
    result = result.filter((t) => t.matchStatus === "needs-review");
  }
  if (state.dateMode === "custom") {
    if (state.fromDate) {
      const from = state.fromDate.split("/").reverse().join("-");
      result = result.filter((t) => t.date >= from);
    }
    if (state.toDate) {
      const to = state.toDate.split("/").reverse().join("-");
      result = result.filter((t) => t.date <= to);
    }
  }
  return result;
}

function readSignerName(): string {
  try {
    const full = localStorage.getItem("full_name") || localStorage.getItem("user_name");
    if (full) return full;
    const first = localStorage.getItem("first_name");
    const email = localStorage.getItem("user_email") || "";
    if (first) return first;
    if (email) return email.split("@")[0];
  } catch {
    // noop
  }
  return "Claire Kemanzi";
}

export default function ReconcileSessionPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const accountId = params.get("accountId") ?? mockAccounts[0]?.id ?? "";
  const periodStart = params.get("periodStart") ?? "2026-10-01";
  const periodEnd = params.get("periodEnd") ?? "2026-10-31";
  const beginningBalance = Number(params.get("beginningBalance") ?? 0);
  const statementEndingBalance = Number(params.get("statementEndingBalance") ?? 0);

  const account = mockAccounts.find((a) => a.id === accountId);
  const currency = account?.currency ?? "CAD";

  const allTransactions: SessionTransactionRich[] = useMemo(
    () => mockSessionTransactionsRich,
    [],
  );

  const [clearedIds, setClearedIds] = useState<Set<string>>(() => new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tabFilter, setTabFilter] = useState<TabFilter>("all");
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [filterState, setFilterState] = useState<SessionFilterState>(EMPTY_FILTER_STATE);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(() => loadVisibleColumns());
  const [density, setDensity] = useState<Density>(() => loadDensity());
  const [tourOpen, setTourOpen] = useState<boolean>(() => !isTourDismissed());
  const [printTick, setPrintTick] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [markModalOpen, setMarkModalOpen] = useState(false);

  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { saveVisibleColumns(visibleColumns); }, [visibleColumns]);
  useEffect(() => { saveDensity(density); }, [density]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const payments = allTransactions.filter((t) => t.amount < 0);
  const deposits = allTransactions.filter((t) => t.amount > 0);
  const paymentsTotal = payments.reduce((s, t) => s + t.amount, 0);
  const depositsTotal = deposits.reduce((s, t) => s + t.amount, 0);

  const clearedTotal = allTransactions.reduce(
    (s, t) => (clearedIds.has(t.id) ? s + t.amount : s),
    0,
  );
  const novalaCleared = Number((beginningBalance + clearedTotal).toFixed(2));
  const difference = Number((statementEndingBalance - novalaCleared).toFixed(2));
  const isBalanced = Math.abs(difference) < 0.01;

  const filteredByTab = filterSessionByType(allTransactions, tabFilter);
  const filteredTransactions = applyFilterState(filteredByTab, filterState, clearedIds);
  const tabCounts = countByTabFilter(allTransactions);

  const payees = useMemo(() => {
    const set = new Set<string>();
    allTransactions.forEach((t) => set.add(t.payee));
    return Array.from(set).sort();
  }, [allTransactions]);

  const toggleCleared = (id: string) => {
    setClearedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onRowClick = (id: string) => {
    setEditingId((current) => (current === id ? null : id));
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = (updated: SessionTransactionRich) => {
    console.log("Save edited transaction:", updated);
    setEditingId(null);
  };
  const openFullEditor = (id: string) => {
    console.log("Open full editor for transaction:", id);
  };

  const onEditInfo = () => navigate("/accounting/reconcile");
  const onFinishNow = () => {
    if (isBalanced) setMarkModalOpen(true);
  };
  const onSaveForLaterAction = () => navigate("/accounting/reconcile");
  const onCloseWithoutSaving = () => navigate("/accounting/reconcile");
  const onPrint = () => {
    setPrintTick((n) => n + 1);
    setTimeout(() => window.print(), 50);
  };
  const openTour = () => setTourOpen(true);
  const closeTour = () => { setTourOpen(false); setTourDismissed(true); };
  const toggleColumn = (col: ColumnKey) => {
    setVisibleColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  };
  const applyFilter = () => setFilterPopoverOpen(false);
  const resetFilter = () => setFilterState(EMPTY_FILTER_STATE);
  const clearStatementChip = () => {
    console.log("Clear statement date chip");
  };
  const clearAllFilters = () => {
    setFilterState(EMPTY_FILTER_STATE);
    setTabFilter("all");
  };

  const handleConfirmLock = ({ confirmed, attachment }: { confirmed: boolean; attachment: File | null }) => {
    if (!confirmed) return;
    console.log("Sign and lock:", { accountId, periodStart, periodEnd, attachment: attachment?.name ?? null });
    if (accountId) clearDraft(accountId);
    setMarkModalOpen(false);
    const q = new URLSearchParams({
      reconciled: "1",
      accountId,
      periodEnd,
    });
    navigate(`/accounting/reconcile?${q.toString()}`);
  };

  const pageStyle: React.CSSProperties = {
    width: "100%",
    margin: 0,
    padding: 0,
    background: "#fff",
    minHeight: "100%",
  };

  const stickyBlockStyle: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "#fff",
    boxShadow: scrolled ? "0 2px 8px rgba(0, 0, 0, 0.04)" : "none",
    transition: "box-shadow 0.15s ease-out",
  };

  return (
    <div style={pageStyle}>
      <div ref={sentinelRef} style={{ height: 1 }} />

      <div style={stickyBlockStyle}>
        <SessionHeader
          account={account}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onEditInfo={onEditInfo}
          onFinishNow={onFinishNow}
          onSaveForLater={onSaveForLaterAction}
          onCloseWithoutSaving={onCloseWithoutSaving}
          finishEnabled={isBalanced}
        />

        <MathEquation
          currency={currency}
          statementEndingBalance={statementEndingBalance}
          clearedTotal={novalaCleared}
          beginningBalance={beginningBalance}
          paymentsTotal={paymentsTotal}
          paymentsCount={payments.length}
          depositsTotal={depositsTotal}
          depositsCount={deposits.length}
          difference={difference}
          isBalanced={isBalanced}
        />

        <SessionFilterBar
          activeTab={tabFilter}
          onTabChange={setTabFilter}
          counts={tabCounts}
          statementDateChipLabel={`Statement period: ${formatDateLong(periodStart)} to ${formatDateLong(periodEnd)}`}
          onToggleFilterPopover={() => { setFilterPopoverOpen((v) => !v); setSettingsOpen(false); }}
          filterPopoverOpen={filterPopoverOpen}
          onClearChip={clearStatementChip}
          onClearAllFilters={clearAllFilters}
          onOpenHowItWorks={openTour}
          onPrint={onPrint}
          onToggleSettings={() => { setSettingsOpen((v) => !v); setFilterPopoverOpen(false); }}
          settingsOpen={settingsOpen}
          filterButtonRef={filterButtonRef}
          settingsButtonRef={settingsButtonRef}
        />
      </div>

      {filterPopoverOpen && (
        <FilterPopover
          state={filterState}
          onChange={setFilterState}
          onApply={applyFilter}
          onReset={resetFilter}
          onClose={() => setFilterPopoverOpen(false)}
          account={account}
          payees={payees}
          anchorRef={filterButtonRef}
        />
      )}

      {settingsOpen && (
        <TableSettings
          visibleColumns={visibleColumns}
          density={density}
          onToggleColumn={toggleColumn}
          onChangeDensity={setDensity}
          onClose={() => setSettingsOpen(false)}
          anchorRef={settingsButtonRef}
        />
      )}

      <SessionTable
        transactions={filteredTransactions}
        clearedIds={clearedIds}
        onToggleCleared={toggleCleared}
        onRowClick={onRowClick}
        editingId={editingId}
        onCancelEdit={cancelEdit}
        onSaveEdit={saveEdit}
        onOpenFullEditor={openFullEditor}
        visibleColumns={visibleColumns}
        density={density}
        currency={currency}
      />

      {tourOpen && <HowItWorksTour onClose={closeTour} />}

      <PrintReport
        key={printTick}
        companyName={COMPANY_NAME}
        account={account}
        periodEnd={periodEnd}
        transactions={filteredTransactions}
        clearedIds={clearedIds}
      />

      <MarkReconciledModal
        open={markModalOpen}
        account={account}
        periodStart={periodStart}
        periodEnd={periodEnd}
        statementEndingBalance={statementEndingBalance}
        novalaCleared={novalaCleared}
        paymentsTotal={paymentsTotal}
        paymentsCount={payments.length}
        depositsTotal={depositsTotal}
        depositsCount={deposits.length}
        currency={currency}
        signerName={readSignerName()}
        onCancel={() => setMarkModalOpen(false)}
        onConfirm={handleConfirmLock}
      />
    </div>
  );
}